'use client'

import { formatCurrency } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { Modal } from '@repo/ui/Modal'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUIStack from '@mui/material/Stack'
import MUITable from '@mui/material/Table'
import MUITableBody from '@mui/material/TableBody'
import MUITableCell from '@mui/material/TableCell'
import MUITableContainer from '@mui/material/TableContainer'
import MUITableHead from '@mui/material/TableHead'
import MUITableRow from '@mui/material/TableRow'
import MUITypography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'

export const VatableDisbursementsModal = () => {
  const { fetchLedgerDisbursements, currentClientMatterNo } = useMattersStore()
  const [open, setOpen] = useState(false)
  const [disbursements, setDisbursements] = useState([])
  const [totalWithVat, setTotalWithVat] = useState(0)
  const [totalWithVatGross, setTotalWithVatGross] = useState(0)
  const [totalWithoutVat, setTotalWithoutVat] = useState(0)
  const [selectedLedgerIdsWithVat, setSelectedLedgerIdsWithVat] = useState('')
  const [selectedLedgerIdsWithoutVat, setSelectedLedgerIdsWithoutVat] = useState('')
  const [requisitionDate, setRequisitionDate] = useState(null)
  const theme = useTheme()
  const fontSize = '0.875rem'

  useEffect(() => {
    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return
      if (!event.data?.type?.startsWith('disbursements:')) return

      const { type, payload } = event.data

      if (type === 'disbursements:open') {
        const {
          currencyCode,
          requisitionDate,
          requisitionType,
          selectedLedgerIdsWithVat,
          selectedLedgerIdsWithoutVat
        } = payload || {}

        console.log('Received from Osprey 1:', {
          currencyCode,
          requisitionDate,
          requisitionType,
          selectedLedgerIdsWithVat,
          selectedLedgerIdsWithoutVat
        })

        setRequisitionDate(requisitionDate)

        const disbursements =
          (await fetchLedgerDisbursements({ clientMatterNo: currentClientMatterNo, currencyCode })) || []

        const selectedLedgerIdsWithVatArray = selectedLedgerIdsWithVat?.length
          ? selectedLedgerIdsWithVat.split(',')
          : []

        const selectedLedgerIdsWithoutVatArray = selectedLedgerIdsWithoutVat?.length
          ? selectedLedgerIdsWithoutVat.split(',')
          : []

        disbursements.forEach((d) => {
          const disbursementId = String(d.ledgerPostingId)

          if (selectedLedgerIdsWithVatArray.includes(disbursementId)) {
            d.selection = 'withVat'
          } else if (selectedLedgerIdsWithoutVatArray.includes(disbursementId)) {
            d.selection = 'withoutVat'
          } else {
            d.selection = null
          }
        })

        setDisbursements(disbursements)
        setOpen(true)
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [fetchLedgerDisbursements, currentClientMatterNo])

  const handleChange = ({ disbursement, type, action }) => {
    const updatedDisbursements = disbursements.map((d) => {
      if (d.ledgerPostingId !== disbursement.ledgerPostingId) {
        return d
      }

      return {
        ...d,
        selection: action === 'add' ? (type === 'withVat' ? 'withVat' : 'withoutVat') : null
      }
    })

    setDisbursements(updatedDisbursements)
  }

  useEffect(() => {
    let totalWithVat = 0
    let totalWithoutVat = 0
    let totalWithVatGross = 0
    let selectedLedgerIdsWithVat = []
    let selectedLedgerIdsWithoutVat = []

    disbursements.forEach((d) => {
      if (d.selection === 'withVat') {
        totalWithVat += d.totalAmount
        totalWithVatGross += d.totalAmount + d.vatAmount
        selectedLedgerIdsWithVat.push(d.ledgerPostingId)
      } else if (d.selection === 'withoutVat') {
        totalWithoutVat += d.totalAmount
        selectedLedgerIdsWithoutVat.push(d.ledgerPostingId)
      }
    })

    setTotalWithVat(totalWithVat)
    setTotalWithVatGross(totalWithVatGross)
    setTotalWithoutVat(totalWithoutVat)
    setSelectedLedgerIdsWithVat(selectedLedgerIdsWithVat.join(','))
    setSelectedLedgerIdsWithoutVat(selectedLedgerIdsWithoutVat.join(','))
  }, [disbursements])

  const onClose = ({ sendUpdateToOsprey1 }) => {
    if (sendUpdateToOsprey1) {
      const { contentWindow } = document.querySelector('iframe[name="ospreyIframe"]') || {}
      if (!contentWindow?.OpenFeeEarnerBillCalculatorResult) return

      const allSelectedDisbursementIds = [
        ...selectedLedgerIdsWithVat.split(','),
        ...selectedLedgerIdsWithoutVat.split(',')
      ]
        .filter(Boolean)
        .join(',')

      // call the Osprey 1 function twice: once for Vatable and once for Non-Vatable
      contentWindow.OpenFeeEarnerBillCalculatorResult(
        'vatabledisbs',
        totalWithVat,
        selectedLedgerIdsWithVat,
        requisitionDate,
        allSelectedDisbursementIds
      )

      contentWindow.OpenFeeEarnerBillCalculatorResult(
        'disbsagency',
        totalWithoutVat,
        selectedLedgerIdsWithoutVat,
        requisitionDate,
        allSelectedDisbursementIds
      )
    }

    setOpen(false)
  }

  return (
    <div data-testid="VatableDisbursementsModal">
      <Modal
        open={open}
        handleClose={() => onClose({ sendUpdateToOsprey1: false })}
        title="Select disbursements"
        sx={{
          maxWidth: '1200px',
          width: 'calc(100% - 24px)',
          height: 'auto',
          maxHeight: 'calc(100% - 24px)',
          overflow: 'auto'
        }}
        theme={theme}
      >
        <MUITableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
          <MUITable stickyHeader sx={{ '& tbody td': { py: 0.5 } }}>
            <MUITableHead>
              <MUITableRow>
                <MUITableCell sx={{ fontWeight: 600, fontSize, width: '150px' }}>Date</MUITableCell>
                <MUITableCell sx={{ fontWeight: 600, fontSize, minWidth: '200px' }}>Detail</MUITableCell>
                <MUITableCell sx={{ fontWeight: 600, fontSize, width: '120px' }}>Reference</MUITableCell>
                <MUITableCell sx={{ textAlign: 'right', fontWeight: 600, fontSize, width: '120px' }}>VAT</MUITableCell>
                <MUITableCell sx={{ textAlign: 'right', fontWeight: 600, fontSize, width: '120px' }}>
                  Amount
                </MUITableCell>
                <MUITableCell
                  sx={{
                    textAlign: 'right',
                    fontWeight: 600,
                    fontSize,
                    width: '180px',
                    backgroundColor: 'background.dark'
                  }}
                >
                  Select with VAT <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>(Vatable Disbursements)</span>
                </MUITableCell>
                <MUITableCell
                  sx={{
                    textAlign: 'right',
                    fontWeight: 600,
                    fontSize,
                    width: '180px',
                    backgroundColor: 'background.dark'
                  }}
                >
                  Select without VAT <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>(Agency Disbursements)</span>
                </MUITableCell>
              </MUITableRow>
            </MUITableHead>

            <MUITableBody>
              {disbursements?.map((disbursement) => {
                const isCheckedWithVat = disbursement.selection === 'withVat'
                const isCheckedWithoutVat = disbursement.selection === 'withoutVat'

                return (
                  <MUITableRow key={disbursement.ledgerPostingId}>
                    <MUITableCell sx={{ fontSize }}>{formatDate(disbursement.postingDate, 'dd MMM yyyy')}</MUITableCell>
                    <MUITableCell sx={{ fontSize }}>{disbursement.description}</MUITableCell>
                    <MUITableCell sx={{ fontSize }}>{disbursement.reference}</MUITableCell>
                    <MUITableCell sx={{ textAlign: 'right', fontSize }}>
                      {disbursement.vatAmount ? formatCurrency(disbursement.vatAmount) : '-'}
                    </MUITableCell>
                    <MUITableCell sx={{ textAlign: 'right', fontSize }}>
                      {formatCurrency(disbursement.totalAmount)}
                    </MUITableCell>

                    <MUITableCell sx={{ textAlign: 'right', backgroundColor: 'background.dark' }}>
                      <MUICheckbox
                        checked={isCheckedWithVat}
                        onChange={() =>
                          handleChange({
                            disbursement,
                            type: 'withVat',
                            action: isCheckedWithVat ? 'remove' : 'add'
                          })
                        }
                      />
                    </MUITableCell>

                    <MUITooltip title={disbursement.expenseClaimVatTreatment === 'REC_VAT_3LEG' ? "Includes reclaimable VAT - must be billed with VAT" : ''}>
                      <MUITableCell sx={{ textAlign: 'right', backgroundColor: 'background.dark' }}>
                          <MUICheckbox
                            checked={isCheckedWithoutVat}
                            disabled={disbursement.expenseClaimVatTreatment === 'REC_VAT_3LEG'}
                            onChange={() =>
                              handleChange({
                                disbursement,
                                type: 'withoutVat',
                                action: isCheckedWithoutVat ? 'remove' : 'add'
                              })
                            }
                          />
                      </MUITableCell>
                    </MUITooltip>
                  </MUITableRow>
                )
              })}

              <MUITableRow>
                <MUITableCell sx={{ fontSize, fontWeight: 600 }}>TOTAL</MUITableCell>
                <MUITableCell></MUITableCell>
                <MUITableCell></MUITableCell>
                <MUITableCell></MUITableCell>
                <MUITableCell></MUITableCell>
                <MUITableCell sx={{ textAlign: 'right', fontSize, fontWeight: 600 }}>
                  <MUITypography variant="body1" sx={{ paddingTop: '8px', fontWeight: 600 }}>NET: {formatCurrency(totalWithVat)}</MUITypography>
                  <MUITypography variant="body1" sx={{ mb: 0, paddingBottom: '8px', fontWeight: 600 }}>GROSS: {formatCurrency(totalWithVatGross)}</MUITypography>
                </MUITableCell>
                <MUITableCell sx={{ textAlign: 'right', fontSize, fontWeight: 600 }}>
                  {formatCurrency(totalWithoutVat)}
                </MUITableCell>
              </MUITableRow>
            </MUITableBody>
          </MUITable>
        </MUITableContainer>

        <MUIStack direction="row" alignItems="center" justifyContent="flex-end" gap={2} sx={{ mt: 6 }}>
          <MUIButton variant="text" onClick={() => onClose({ sendUpdateToOsprey1: false })}>
            Cancel
          </MUIButton>
          <MUIButton variant="contained" onClick={() => onClose({ sendUpdateToOsprey1: true })}>
            Save
          </MUIButton>
        </MUIStack>
      </Modal>
    </div>
  )
}
